import cx from 'classnames';
import { DateTime } from 'luxon';
import styled from '@emotion/styled';
import React, { memo, ReactElement, useContext, useMemo } from 'react';

import { assetUrl } from '~/helpers';
import { palette } from '~/utils/colours';
import UserContext from '~/App/Contexts/UserContext';
import EditorContext, { EditorContextProps } from '../../Editor/Context';

const Divider = styled.div`
  border-bottom: 1px solid ${palette.veryLightPink};
  width: calc(100% + 20px);
  margin-left: -10px;
`;

const Container = styled.div`
  padding-top: 40px;
  border-radius: 6px;
  background: url(${({ background }: { background: string }) => background})
    no-repeat;
  background-size: cover;
  max-width: 775px;
  margin: auto;
  height: 640px;
  .mail-preview-body {
    overflow: auto;
    height: 525px;
  }
  &.mobile {
    padding-top: 10px;
    height: 500px;
    .mail-preview-body {
      height: 455px;
    }
  }
`;

const SubjectContainer = styled.div`
  .subject-title {
    height: unset !important;
  }
`;

const Subject = styled.div`
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 0.08px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${palette.$darkGrey};
`;

const HeaderContainer = styled.div`
  margin: 10px 0 !important;
`;

const SenderName = styled(Subject)`
  span {
    font-size: 12px;
    letter-spacing: 0.07px;
    color: #6d7278;
  }
`;

const Body = styled.div`
  .resend-body {
    margin-top: 25px;
  }
`;

function getTimestamp() {
  return DateTime.local().minus({ hour: 1 }).toLocaleString(DateTime.DATE_MED);
}

function EmailBody(props: EmailBodyProps) {
  const { name, subject, body } = props;
  const editorContext: EditorContextProps = useContext(EditorContext);
  const { preview } = editorContext;
  const isMobile = useMemo(() => {
    return preview === 'mobile';
  }, [preview]);

  const previewBackground = useMemo(() => {
    return isMobile
      ? null
      : assetUrl('assets/images/message-template/email-desktop.svg');
  }, [isMobile]);

  return (
    <Container
      className={cx('resend-mail-preview', { mobile: isMobile })}
      background={previewBackground}
    >
      <SubjectContainer className='mail-preview-subject'>
        <div className='subject-title'>
          <Subject>{subject}</Subject>
        </div>
      </SubjectContainer>
      <Divider />
      {!isMobile && (
        <>
          <HeaderContainer className='mail-preview-header'>
            <div className='mail-preview-sender'>
              <SenderName>
                {name} <span>&lt;senderfrom@email.com&gt;</span>
              </SenderName>
            </div>
            <div className='mail-preview-date'>
              <div className='date-inner'>{getTimestamp()}</div>
            </div>
          </HeaderContainer>

          <Divider />
        </>
      )}
      <Body className='mail-preview-body'>
        <div className='my-4'>{body}</div>
      </Body>
    </Container>
  );
}

const Email = memo((props: EmailProps) => {
  return (
    <UserContext.Consumer>
      {(user) => <EmailBody timezone={user.timezone} {...props} />}
    </UserContext.Consumer>
  );
});

Email.displayName = 'Email';

export default Email;

export interface EmailProps {
  name?: string;
  image?: string;
  subject?: string;
  body: ReactElement;
}
export interface EmailBodyProps extends EmailProps {
  timezone: string;
}
